import React, { useEffect, useState } from 'react';
import { Card, Statistic, Tooltip, Spin, Button } from 'antd';
import { WalletOutlined, LockOutlined, UnlockOutlined, ReloadOutlined } from '@ant-design/icons';
import { getPledgeSummary } from '@/services/ant-design-pro/pledge';

interface PledgeSummaryProps {
    className?: string;
}

const PledgeSummary: React.FC<PledgeSummaryProps> = ({ className }) => {
    const [loading, setLoading] = useState(false);
    const [pledgeData, setPledgeData] = useState<API.PledgeSummaryDto | null>(null);
    const [error, setError] = useState<string | null>(null);

    const fetchPledgeSummary = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getPledgeSummary();
            if (response.code === 0 && response.data) {
                setPledgeData(response.data);
            } else {
                setError(response.msg || '获取质押金信息失败');
            }
        } catch (err) {
            setError('获取质押金信息失败');
            console.error('Failed to fetch pledge summary:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        fetchPledgeSummary();
    };

    useEffect(() => {
        // Initial fetch
        fetchPledgeSummary();

        // Set up auto-refresh every minute (60000ms)
        const intervalId = setInterval(() => {
            fetchPledgeSummary();
        }, 60000);

        // Cleanup interval on component unmount
        return () => {
            clearInterval(intervalId);
        };
    }, []);

    if (loading) {
        return (
            <div className={className} style={{ display: 'flex', alignItems: 'center' }}>
                <Spin size="small" />
            </div>
        );
    }

    if (error || !pledgeData) {
        return (
            <Tooltip title={error || '暂无质押金信息'}>
                <div className={className} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <WalletOutlined style={{ fontSize: '16px', color: '#999' }} />
                </div>
            </Tooltip>
        );
    }

    const formatAmount = (amount: string) => {
        const num = parseFloat(amount);
        if (isNaN(num)) return '0.00';
        return num.toFixed(2);
    };

    const totalAmount = parseFloat(pledgeData.available_amount) + parseFloat(pledgeData.frozen_amount);

    return (
        <Tooltip
            title={
                <div>
                    <div>总质押金: {formatAmount(totalAmount.toString())} {pledgeData.currency}</div>
                    <div>可用金额: {formatAmount(pledgeData.available_amount)} {pledgeData.currency}</div>
                    <div>冻结金额: {formatAmount(pledgeData.frozen_amount)} {pledgeData.currency}</div>
                </div>
            }
            placement="bottom"
        >
            <div
                className={className}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#f8f9fa'
                }}
            >
                <WalletOutlined style={{ fontSize: '14px', color: '#1890ff' }} />
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {formatAmount(pledgeData.available_amount)}
                </span>
                <span style={{ fontSize: '10px', color: '#999' }}>
                    {pledgeData.currency}
                </span>
                <Button
                    type="text"
                    size="small"
                    icon={<ReloadOutlined style={{ fontSize: '10px' }} />}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRefresh();
                    }}
                    style={{
                        padding: '0',
                        height: 'auto',
                        minWidth: 'auto',
                        color: '#1890ff'
                    }}
                />
            </div>
        </Tooltip>
    );
};

export default PledgeSummary; 